/**
 * The check as a guided form (screens §A1): an intro, one item at a time with a progress strip, then a review that lists
 * unanswered items before Submit. Answers autosave as they become complete (`assessmentAnswer`); a resumed attempt reopens
 * on its first unanswered item. `timeBudgetMinutes` is guidance, never enforced.
 */
import { useRef, useState } from "react";
import type { AssessmentAttempt, ErrorBody } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import { Link } from "../shell/history";
import { GxCard, GxErrorBanner } from "../theme/primitives";
import { GxProgressSteps } from "../theme/ProgressSteps";
import { GxStickyBar } from "../theme/GxStickyBar";
import { answerFromDraft, CONFIDENCE_MISSING_LINE, isOverLength, OVER_LENGTH_LINE, draftFromAnswer, EMPTY_DRAFT, needsConfidence, type Draft } from "./answers";
import { CHECK_COPY } from "./copy";
import { FactSheet } from "./FactSheet";
import { ItemView } from "./ItemView";

const TEXT_AUTOSAVE_MS = 800;
const MAX_SPENT_MS = 3_600_000;
const INTRO = -1;

type SaveState = { readonly kind: "idle" | "saving" | "saved" } | { readonly kind: "error"; readonly error: ErrorBody["error"] };

/** "Situation 3 of 10" / "Judgment case 1 of 2": the item's place among items of its kind. */
const itemLabel = (attempt: AssessmentAttempt, index: number): string => {
  const scored = attempt.items[index]?.scored !== false;
  const total = attempt.items.filter((i) => i.scored === scored).length;
  const n = attempt.items.slice(0, index + 1).filter((i) => i.scored === scored).length;
  return `${scored ? "Situation" : "Judgment case"} ${n} of ${total}`;
};

/** Where "Later" leads on an optional check's intro, and what the destination is called. */
export interface CheckExit { readonly href: string; readonly label: string; /** A line under the buttons about what leaving does. */ readonly note?: string; /** Run when the person takes the exit. */ readonly onTake?: () => void }

export function CheckForm({ attempt, api, onSubmitted, later, solo = false }: { readonly attempt: AssessmentAttempt; readonly api: ApiClient; readonly onSubmitted: () => void; readonly later?: CheckExit | undefined; readonly solo?: boolean }) {
  const { items } = attempt;
  const [drafts, setDrafts] = useState<Readonly<Record<string, Draft>>>(() => Object.fromEntries(attempt.answers.flatMap((a) => {
    const item = items.find((i) => i.itemId === a.itemId);
    return item === undefined ? [] : [[a.itemId, draftFromAnswer(item, a.answer)] as const];
  })));
  const draftOf = (itemId: string): Draft => drafts[itemId] ?? EMPTY_DRAFT;
  const answered = items.map((item) => answerFromDraft(item, draftOf(item.itemId)) !== null);
  const answeredCount = answered.filter(Boolean).length;

  // Resume: an untouched attempt starts at the intro, otherwise at the first unanswered item (or the review).
  const [step, setStep] = useState(() => (attempt.answers.length === 0 ? INTRO : answered.includes(false) ? answered.indexOf(false) : items.length));
  const [save, setSave] = useState<SaveState>({ kind: "idle" });
  const [submitError, setSubmitError] = useState<ErrorBody["error"] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const spentMs = useRef<Record<string, number>>({});
  const enteredAt = useRef(Date.now());
  const pendingText = useRef<{ readonly timer: ReturnType<typeof setTimeout>; readonly run: () => void } | null>(null);

  const item = items[step];
  const saveAnswer = (itemId: string, draft: Draft) => {
    const source = items.find((i) => i.itemId === itemId);
    const answer = source === undefined ? null : answerFromDraft(source, draft);
    if (answer === null) return;
    const timeSpentMs = Math.min(MAX_SPENT_MS, (spentMs.current[itemId] ?? 0) + (item?.itemId === itemId ? Date.now() - enteredAt.current : 0));
    setSave({ kind: "saving" });
    void api.call("assessmentAnswer", { attemptId: attempt.id, itemId }, { answer, timeSpentMs }).then((r) => setSave(r.ok ? { kind: "saved" } : { kind: "error", error: r.error }));
  };

  const commit = () => {
    const pending = pendingText.current;
    if (pending === null) return;
    clearTimeout(pending.timer);
    pendingText.current = null;
    pending.run();
  };

  const change = (itemId: string, next: Draft) => {
    setDrafts((d) => ({ ...d, [itemId]: next }));
    if (items.find((i) => i.itemId === itemId)?.kind !== "judgment_case") { saveAnswer(itemId, next); return; }
    if (pendingText.current !== null) clearTimeout(pendingText.current.timer);
    const run = () => saveAnswer(itemId, next);
    pendingText.current = { run, timer: setTimeout(() => { pendingText.current = null; run(); }, TEXT_AUTOSAVE_MS) };
  };

  const [left, setLeft] = useState<string | null>(null);
  const go = (to: number) => {
    commit();
    // Moving on from an unanswered item never blocks; the next screen says once that it is waiting on the list at the end.
    setLeft(to > step && item !== undefined && !answered[step] ? itemLabel(attempt, step) : null);
    if (item !== undefined) spentMs.current[item.itemId] = (spentMs.current[item.itemId] ?? 0) + Date.now() - enteredAt.current;
    enteredAt.current = Date.now();
    setStep(Math.max(INTRO, Math.min(items.length, to)));
  };

  // Submit sends no text: it submits what was saved, so it waits while any written answer on screen is past its maximum.
  const anyOver = items.some((it) => isOverLength(it, draftOf(it.itemId)));
  const submit = async () => {
    if (anyOver) return;
    commit();
    setSubmitting(true);
    const r = await api.call("assessmentSubmit", { attemptId: attempt.id }, undefined);
    setSubmitting(false);
    if (r.ok) onSubmitted(); else setSubmitError(r.error);
  };

  const copy = CHECK_COPY[attempt.phase];
  const steps = items.map((_, i) => ({ label: itemLabel(attempt, i), done: answered[i] ?? false }));
  const unanswered = items.flatMap((_, i) => (answered[i] ? [] : [i]));

  if (step === INTRO) {
    return (
      <GxCard className="gx-ci-card gx-ci-intro" testId="check.intro">
        <div className="gx-eyebrow">{attempt.phase === "pre" ? "Before you start" : solo ? "After your game" : "After the day"}</div>
        <h1 className="gx-h1">{copy.title}</h1>
        <p className="gx-story gx-ci-lede">{copy.intro}</p>
        <ul className="gx-ci-facts">
          <li><b>{items.filter((i) => i.scored).length}</b> situations, each with a confidence rating</li>
          <li><b>{items.filter((i) => !i.scored).length}</b> judgment cases in your own words</li>
          <li>About <b>{attempt.timeBudgetMinutes}</b> minutes. Take the time you need</li>
        </ul>
        <div className="gx-ci-actions">
          <button type="button" className="gx-btn gx-btn--primary gx-btn--lg" data-testid="check.begin" onClick={() => go(0)} autoFocus>{attempt.phase === "post" && later !== undefined ? "Take the post-check" : "Begin"}</button>
          {later === undefined ? null : <Link href={later.href} className="gx-btn gx-btn--secondary gx-btn--lg" data-testid="check.later" onClick={later.onTake}>{later.label}</Link>}
        </div>
        {later?.note === undefined ? null : <p className="gx-small gx-muted" data-testid="check.later.note">{later.note}</p>}
      </GxCard>
    );
  }

  return (
    <div className="gx-ci-flow" data-testid="check.form" data-step={step}>
      <div className="gx-ci-progress">
        <div className="gx-ci-progress-h">
          <span className="gx-eyebrow">{item === undefined ? "Review" : itemLabel(attempt, step)}</span>
          <span className="gx-ci-count gx-num" data-testid="check.progress">{answeredCount} of {items.length} questions answered</span>
        </div>
        <GxProgressSteps steps={steps} current={step} onSelect={go} />
      </div>

      <FactSheet bankName={attempt.bankName} text={attempt.factSheet} open={step === 0} />

      {item === undefined ? (
        <GxCard className="gx-ci-card" testId="check.review">
          <h2 className="gx-h2">{unanswered.length === 0 ? "Everything is answered." : `${unanswered.length} still to answer`}</h2>
          {unanswered.length === 0 ? <p className="gx-muted">Submit when you are ready. Your answers stay private.</p> : (
            <ul className="gx-ci-unanswered" data-testid="check.unanswered">
              {unanswered.map((i) => {
                const it = items[i];
                return <li key={it?.itemId}><button type="button" className="gx-linkrow" onClick={() => go(i)}>{itemLabel(attempt, i)}</button>{it !== undefined && needsConfidence(it, draftOf(it.itemId)) ? <span className="gx-faint" data-testid="check.unanswered.confidence"> · {CONFIDENCE_MISSING_LINE}</span> : null}{it !== undefined && isOverLength(it, draftOf(it.itemId)) ? <span className="gx-faint" data-testid="check.unanswered.over"> · {OVER_LENGTH_LINE}</span> : null}</li>;
              })}
            </ul>
          )}
          {anyOver ? <p className="gx-small gx-muted" role="status" data-testid="check.over">{OVER_LENGTH_LINE}</p> : null}
          {submitError === null ? null : <GxErrorBanner error={submitError} />}
        </GxCard>
      ) : (
        <GxCard className="gx-ci-card">
          <ItemView key={item.itemId} item={item} draft={draftOf(item.itemId)} onChange={(next) => change(item.itemId, next)} onCommit={commit} />
        </GxCard>
      )}

      {left === null ? null : <p className="gx-small gx-muted" role="status" data-testid="check.left">{left} is not answered yet; it will be listed at the end.</p>}
      {save.kind === "error" ? <GxErrorBanner error={save.error} /> : null}
      <GxStickyBar className="gx-ci-nav"
        lead={<><button type="button" className="gx-btn gx-btn--ghost gx-btn--lg" onClick={() => go(step - 1)} disabled={step === 0} data-testid="check.back">Back</button>
          <span className="gx-ci-saved gx-faint" role="status" data-testid="check.saved">{save.kind === "saving" ? "Saving" : save.kind === "saved" ? "Saved ✓" : ""}</span></>}>
        {item === undefined
          ? <button type="button" className="gx-btn gx-btn--primary gx-btn--lg" onClick={() => void submit()} disabled={submitting || anyOver} data-testid="check.submit">Submit</button>
          : <button type="button" className="gx-btn gx-btn--primary gx-btn--lg" onClick={() => go(step + 1)} data-testid="check.next">{step === items.length - 1 ? "Review" : "Next"}</button>}
      </GxStickyBar>
    </div>
  );
}
